import type { ChipCategory, ChipOption, OrgType } from "../types";

// Step 2: Current Tool
export const currentToolOptions: Record<OrgType, ChipOption[]> = {
  msp: [
    { label: "Autotask", value: "autotask" },
    { label: "ConnectWise", value: "connectwise" },
    { label: "HaloPSA", value: "halopsa" },
    { label: "SuperOps", value: "superops" },
    { label: "Syncro", value: "syncro" },
    { label: "NinjaOne", value: "ninjaone" },
    { label: "Kaseya BMS", value: "kaseya_bms" },
    { label: "Kaseya Vorex", value: "kaseya_vorex" },
    { label: "Freshservice", value: "freshservice" },
    { label: "ServiceNow", value: "servicenow" },
    { label: "Zendesk", value: "zendesk" },
    { label: "Zammad", value: "zammad" },
    { label: "N-able MSP Manager", value: "nable_msp_manager" },
    { label: "Atera", value: "atera" },
    { label: "Excel/Manual", value: "excel_manual" },
    { label: "None", value: "none" },
    { label: "Other", value: "other" },
  ],
  internal_it: [
    { label: "ServiceNow", value: "servicenow" },
    { label: "Freshservice", value: "freshservice" },
    { label: "Jira Service Management", value: "jira_service_management" },
    { label: "Zendesk", value: "zendesk" },
    { label: "HaloITSM", value: "haloitsm" },
    { label: "ManageEngine", value: "manageengine" },
    { label: "Spiceworks", value: "spiceworks" },
    { label: "Excel/Email", value: "excel_email" },
    { label: "None", value: "none" },
    { label: "Other", value: "other" },
  ],
};

// Step 3: Team Size
export const teamSizeOptions: ChipOption[] = [
  { label: "1-3", value: "1-3" },
  { label: "4-10", value: "4-10" },
  { label: "11-25", value: "11-25" },
  { label: "26-50", value: "26-50" },
  { label: "50+", value: "50+" },
];

export const endUserSizeOptions: Record<OrgType, ChipOption[]> = {
  msp: [
    { label: "1-100", value: "1-100" },
    { label: "101-500", value: "101-500" },
    { label: "501-2000", value: "501-2000" },
    { label: "2000+", value: "2000+" },
  ],
  internal_it: [
    { label: "1-50", value: "1-50" },
    { label: "51-200", value: "51-200" },
    { label: "201-1000", value: "201-1000" },
    { label: "1000+", value: "1000+" },
  ],
};

export const teamSizeLabels: Record<
  OrgType,
  { title: string; subtitle: string; teamLabel: string; endUserLabel: string }
> = {
  msp: {
    title: "Tell us about your MSP",
    subtitle:
      "Help us understand the size of your MSP so we can recommend the right plan and set up your onboarding experience accordingly.",
    teamLabel: "How many team members do you have?",
    endUserLabel: "How many end users do you support?",
  },
  internal_it: {
    title: "Tell us about your team",
    subtitle:
      "Help us understand the size of your team so we can recommend the right plan and set up your onboarding experience accordingly.",
    teamLabel: "How many team members do you have?",
    endUserLabel: "How many employees do you support?",
  },
};

// Step 4: Improvements
export const improvementOptions: Record<OrgType, ChipOption[]> = {
  msp: [
    {
      label: "Replacing our legacy PSA with a modern platform",
      value: "replacing_our_legacy_psa_with_a_modern_platform",
    },
    {
      label: "Better mobile experience for technicians",
      value: "better_mobile_experience_for_technicians",
    },
    {
      label: "Cleaner, faster UI that techs actually enjoy using",
      value: "cleaner_faster_ui_that_techs_actually_enjoy_using",
    },
    {
      label: "Faster response and resolution time",
      value: "faster_response_and_resolution_time",
    },
    {
      label: "Giving end users a modern support experience",
      value: "giving_end_users_a_modern_support_experience",
    },
    {
      label: "Smarter dispatching and workload balancing",
      value: "smarter_dispatching_and_workload_balancing",
    },
    { label: "Better billing system", value: "better_billing_system" },
    { label: "Automating cloud billing", value: "automating_cloud_billing" },
    {
      label: "Closing integration gaps across our stack",
      value: "closing_integration_gaps_across_our_stack",
    },
    {
      label: "AI powered ticket triage and routing",
      value: "ai_powered_ticket_triage_and_routing",
    },
    { label: "Other", value: "other" },
  ],
  internal_it: [
    {
      label: "Replacing a legacy ITSM with a modern platform",
      value: "replace_legacy_itsm_with_a_modern_platform",
    },
    {
      label: "Reducing ticket background and response time",
      value: "reducing_ticket_background_and_response_time",
    },
    {
      label: "Cleaner, faster UI for the helpdesk team",
      value: "cleaner_faster_ui_for_the_helpdesk_team",
    },
    {
      label: "Giving users a modern way to raise requests",
      value: "giving_users_a_modern_way_to_raise_requests",
    },
    {
      label: "Better SLA tracking and reporting",
      value: "better_sla_tracking_and_reporting",
    },
    {
      label: "Streamlining the change management process",
      value: "streamlining_the_change_management_process",
    },
    {
      label: "AI-powered ticket triage and routing",
      value: "ai_powered_ticket_triage_and_routing",
    },
    {
      label: "Reducing repeat tickets with better knowledge base",
      value: "reducing_repeat_tickets_with_better_knowledge_base",
    },
    {
      label: "Smarter workload balancing across the team",
      value: "smarter_workload_balancing_across_the_team",
    },
    {
      label: "Asset tracking and lifecycle management",
      value: "asset_tracking_and_lifecycle_management",
    },
    { label: "Other", value: "other" },
  ],
};

// Step 5: Integrations
export const integrationCategories: Record<OrgType, ChipCategory[]> = {
  msp: [
    {
      key: "rmm",
      label: "RMM*",
      options: [
        { label: "Datto RMM", value: "datto_rmm", badge: "LIVE" },
        { label: "NinjaOne", value: "ninjaone", badge: "LIVE" },
        { label: "Level.io", value: "level", badge: "LIVE" },
        { label: "N-able N-central", value: "nable_ncentral", badge: "LIVE" },
        { label: "N-able N-sight", value: "nable_nsight" },
        { label: "ConnectWise RMM", value: "connectwise_rmm" },
        { label: "Syncro", value: "syncro" },
        { label: "SuperOps", value: "superops" },
        { label: "Atera", value: "atera" },
        { label: "Kaseya VSA", value: "kaseya_vsa" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "documentation",
      label: "Documentation*",
      options: [
        { label: "Hudu", value: "hudu", badge: "LIVE" },
        { label: "IT Glue", value: "it_glue" },
        { label: "Passportal", value: "passportal" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "accounting",
      label: "Accounting*",
      options: [
        { label: "QuickBooks", value: "quickbooks", badge: "LIVE" },
        { label: "Xero", value: "xero", badge: "LIVE" },
        { label: "Zoho Books", value: "zoho_books" },
        { label: "Wave", value: "wave" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "cloud_licensing",
      label: "Cloud/Licensing*",
      options: [
        { label: "Pax8", value: "pax8", badge: "Coming soon" },
        { label: "Microsoft 365", value: "microsoft_365" },
        { label: "Google Workspace", value: "google_workspace" },
        { label: "Ingram Micro", value: "ingram_micro" },
        { label: "TD Synnex", value: "td_synnex" },
        { label: "D&H", value: "dnh" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "bcdr",
      label: "BCDR",
      options: [
        { label: "Veeam", value: "veeam" },
        { label: "Acronis", value: "acronis" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "payment_gateway",
      label: "Payment gateway",
      options: [
        { label: "Stripe", value: "stripe", badge: "Beta" },
        { label: "Alternative Payments", value: "alternative_payments" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "remote_control",
      label: "Remote control",
      options: [
        { label: "ScreenConnect", value: "screenconnect" },
        { label: "Splashtop", value: "splashtop" },
        { label: "TeamViewer", value: "teamviewer" },
        { label: "GoTo", value: "goto" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
  ],
  internal_it: [
    {
      key: "remote_control",
      label: "Remote control*",
      options: [
        { label: "Screenconnect", value: "screenconnect" },
        { label: "Splashtop", value: "splashtop" },
        { label: "Teamviewer", value: "teamviewer" },
        { label: "GoTo", value: "goto" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "productivity",
      label: "Productivity*",
      options: [
        { label: "Microsoft 365", value: "microsoft_365" },
        { label: "Google Workspace", value: "google_workspace" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "identity_access",
      label: "Identity & Access",
      options: [
        { label: "Microsoft Entra", value: "microsoft_entra" },
        { label: "Okta", value: "okta" },
        { label: "OneLogin", value: "onelogin" },
        { label: "JumpCloud", value: "jumpcloud" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "endpoint_management",
      label: "Endpoint management",
      options: [
        { label: "Microsoft Intune", value: "microsoft_intune" },
        { label: "Jamf", value: "jamf" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "rmm",
      label: "RMM",
      options: [
        { label: "Datto RMM", value: "datto_rmm", badge: "LIVE" },
        { label: "NinjaOne", value: "ninjaone", badge: "LIVE" },
        { label: "Level.io", value: "level", badge: "LIVE" },
        { label: "N-able N-central", value: "nable_ncentral", badge: "LIVE" },
        { label: "N-able N-sight", value: "nable_nsight" },
        { label: "ConnectWise RMM", value: "connectwise_rmm" },
        { label: "Syncro", value: "syncro" },
        { label: "SuperOps", value: "super_ops" },
        { label: "Atera", value: "atera" },
        { label: "Kaseya VSA", value: "kaseya_vsa" },
        { label: "None", value: "none" },
        { label: "Other", value: "other" },
      ],
    },
  ],
};

// Step 6: Referral Source
export const referralOptions: ChipOption[] = [
  { label: "AI search", value: "ai_search" },
  { label: "Google search", value: "google_search" },
  { label: "LinkedIn", value: "linkedin" },
  { label: "Referral from a colleague", value: "referral" },
  { label: "Reddit", value: "reddit" },
  { label: "Facebook", value: "facebook" },
  { label: "Event/Conference", value: "event_conference" },
  { label: "DeskDay demo", value: "deskday_demo" },
  { label: "Other", value: "other" },
];

// Step 2: Subtitles
export const currentToolSubtitles: Record<OrgType, string> = {
  msp: "Let us know which PSA or ticketing system you're currently using. This helps us plan your data migration and get you up and running faster.",
  internal_it:
    "Let us know which ITSM tool you're currently using. This helps us plan your data migration and get you up and running faster.",
};
